import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Checkbox,
  FormControlLabel,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  TextField,
  Typography,
} from "@mui/material";

import Page from "./Page";
import useData from "../../hooks/useData";

const ClientPortalCatalog = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { executeAction } = useData();

  const [modules, setModules] = useState([]);
  const [isLoadingCatalog, setIsLoadingCatalog] = useState(false);
  const [selectedCatalogKey, setSelectedCatalogKey] = useState(null);
  const [moduleForm, setModuleForm] = useState({
    moduleKey: "",
    name: "",
    name_fr: "",
    enabled: true,
    sortOrder: 0,
    presetText: "{}",
  });

  const sortedModules = useMemo(
    () =>
      [...modules].sort((left, right) => {
        const leftOrder = Number(left?.sortOrder || 0);
        const rightOrder = Number(right?.sortOrder || 0);
        if (leftOrder === rightOrder) {
          return String(left?.name || "").localeCompare(
            String(right?.name || ""),
          );
        }
        return leftOrder - rightOrder;
      }),
    [modules],
  );

  const resetModuleForm = () => {
    setSelectedCatalogKey(null);
    setModuleForm({
      moduleKey: "",
      name: "",
      name_fr: "",
      enabled: true,
      sortOrder: 0,
      presetText: "{}",
    });
  };

  const loadCatalog = async () => {
    setIsLoadingCatalog(true);
    try {
      const result = await executeAction({
        entityName: "Client",
        action: "getPortalModuleCatalog",
      });

      if (result?.isSuccessful) {
        const catalogModules = Array.isArray(result?.data?.modules)
          ? result.data.modules
          : [];
        setModules(catalogModules);

        if (!selectedCatalogKey && catalogModules.length > 0) {
          const first = catalogModules[0];
          setSelectedCatalogKey(first.moduleKey);
          setModuleForm({
            moduleKey: first.moduleKey,
            name: first.name || "",
            name_fr: first.name_fr || "",
            enabled: first.enabled !== false,
            sortOrder: Number(first.sortOrder || 0),
            presetText: JSON.stringify(first.preset || {}, null, 2),
          });
        }
      } else {
        setModules([]);
      }
    } finally {
      setIsLoadingCatalog(false);
    }
  };

  useEffect(() => {
    loadCatalog();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCatalogSelect = (module) => {
    setSelectedCatalogKey(module.moduleKey);
    setModuleForm({
      moduleKey: module.moduleKey,
      name: module.name || "",
      name_fr: module.name_fr || "",
      enabled: module.enabled !== false,
      sortOrder: Number(module.sortOrder || 0),
      presetText: JSON.stringify(module.preset || {}, null, 2),
    });
  };

  const handleToolbarClick = async (item) => {
    switch (item) {
      case "save": {
        let preset;
        try {
          preset = JSON.parse(moduleForm.presetText || "{}");
        } catch (_err) {
          await executeAction({
            entityName: "Client",
            action: "getPortalModuleCatalog",
            displayMessage: true,
            customMessage: "Preset JSON is invalid",
          });
          return;
        }

        const nextModules = [...modules];
        const normalizedKey = String(moduleForm.moduleKey || "").trim();
        const normalizedName = String(moduleForm.name || "").trim();

        if (!normalizedKey || !normalizedName) {
          await executeAction({
            entityName: "Client",
            action: "getPortalModuleCatalog",
            displayMessage: true,
            customMessage: "Module key and name are required",
          });
          return;
        }

        const existingIndex = nextModules.findIndex(
          (module) => module.moduleKey === selectedCatalogKey,
        );

        const payloadModule = {
          moduleKey: normalizedKey,
          name: normalizedName,
          name_fr: String(moduleForm.name_fr || "").trim(),
          enabled: moduleForm.enabled !== false,
          sortOrder: Number(moduleForm.sortOrder || 0),
          preset,
        };

        if (existingIndex >= 0) {
          nextModules[existingIndex] = payloadModule;
        } else {
          nextModules.push(payloadModule);
        }

        const uniqueModules = [];
        const keySet = new Set();
        for (const module of nextModules) {
          if (keySet.has(module.moduleKey)) continue;
          uniqueModules.push(module);
          keySet.add(module.moduleKey);
        }

        const catalogSave = await executeAction({
          entityName: "Client",
          action: "savePortalModuleCatalog",
          data: { modules: uniqueModules },
          displayMessage: true,
        });

        if (!catalogSave?.isSuccessful) return;

        await loadCatalog();
        setSelectedCatalogKey(normalizedKey);
        break;
      }

      case "newModule":
        resetModuleForm();
        break;

      case "deleteModule": {
        if (!selectedCatalogKey) return;

        const nextModules = modules.filter(
          (module) => module.moduleKey !== selectedCatalogKey,
        );

        const catalogSave = await executeAction({
          entityName: "Client",
          action: "savePortalModuleCatalog",
          data: { modules: nextModules },
          displayMessage: true,
        });

        if (!catalogSave?.isSuccessful) return;

        await loadCatalog();
        resetModuleForm();
        break;
      }

      case "refresh":
        await loadCatalog();
        break;

      case "assignments":
        navigate("/client-portal-modules", {
          state: {
            clientId: location?.state?.clientId || null,
            clientName: location?.state?.clientName || "",
          },
        });
        break;

      default:
        break;
    }
  };

  return (
    <Page
      pageName="clientPortalCatalog"
      includeToolbar={true}
      onToolItemClick={handleToolbarClick}
      items={[
        { name: "assignments", iconName: "ToggleOn" },
        { name: "newModule", iconName: "AddCircleOutline" },
        { name: "deleteModule", iconName: "DeleteOutline" },
        { name: "save", iconName: "Save" },
        { name: "refresh", iconName: "Refresh" },
      ]}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Paper sx={{ p: 2 }}>
          <Typography
            variant="h6"
            sx={{ mb: 2 }}
          >
            Global Module Catalog (Settings)
          </Typography>

          <Box sx={{ display: "flex", gap: 2 }}>
            <Box sx={{ flex: 1, minWidth: 260 }}>
              {isLoadingCatalog ? (
                <Typography color="text.secondary">
                  Loading catalog...
                </Typography>
              ) : sortedModules.length === 0 ? (
                <Typography color="text.secondary">
                  No modules in catalog. Create one with the toolbar add button.
                </Typography>
              ) : (
                <List>
                  {sortedModules.map((module) => (
                    <ListItem
                      key={module.moduleKey}
                      disablePadding
                    >
                      <ListItemButton
                        selected={selectedCatalogKey === module.moduleKey}
                        onClick={() => handleCatalogSelect(module)}
                      >
                        <ListItemText
                          primary={`${module.name || module.moduleKey} (${module.moduleKey})`}
                          secondary={`enabled: ${module.enabled === false ? "no" : "yes"} | order: ${module.sortOrder || 0}`}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>

            <Box
              sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1 }}
            >
              <TextField
                size="small"
                label="Module Key"
                value={moduleForm.moduleKey}
                onChange={(e) =>
                  setModuleForm((current) => ({
                    ...current,
                    moduleKey: e.target.value,
                  }))
                }
              />
              <TextField
                size="small"
                label="Name"
                value={moduleForm.name}
                onChange={(e) =>
                  setModuleForm((current) => ({
                    ...current,
                    name: e.target.value,
                  }))
                }
              />
              <TextField
                size="small"
                label="Name (fr)"
                value={moduleForm.name_fr}
                onChange={(e) =>
                  setModuleForm((current) => ({
                    ...current,
                    name_fr: e.target.value,
                  }))
                }
              />
              <TextField
                size="small"
                label="Sort Order"
                type="number"
                value={moduleForm.sortOrder}
                onChange={(e) =>
                  setModuleForm((current) => ({
                    ...current,
                    sortOrder: Number(e.target.value || 0),
                  }))
                }
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={moduleForm.enabled}
                    onChange={(e) =>
                      setModuleForm((current) => ({
                        ...current,
                        enabled: e.target.checked,
                      }))
                    }
                  />
                }
                label="Enabled Globally"
              />
              <TextField
                multiline
                minRows={7}
                label="Preset (JSON)"
                value={moduleForm.presetText}
                onChange={(e) =>
                  setModuleForm((current) => ({
                    ...current,
                    presetText: e.target.value,
                  }))
                }
              />
            </Box>
          </Box>
        </Paper>
      </Box>
    </Page>
  );
};

export default ClientPortalCatalog;
